import {
    Box,
    Button,
    Heading,
    Text,
    VStack,
    HStack,
    Badge,
    Progress,
    IconButton,
    Textarea,
    useToast,
    Spinner,
    Collapse,
    Flex,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useColorModeValue,
    Icon,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { MdCheckCircle, MdError, MdRemoveCircle, MdEdit, MdAutoAwesome, MdDelete } from 'react-icons/md';
import { useComplianceItems, useCreateComplianceItems, useUpdateComplianceItem, useDeleteComplianceItem } from '../../services/hooks';

interface BidPrepTabProps {
    pursuitId: number;
    importedItemId: number; // For AI context
}

export function BidPrepTab({ pursuitId, importedItemId }: BidPrepTabProps) {
    const toast = useToast();
    const panelBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    // Data Hooks
    // Data Hooks
    const [pollCount, setPollCount] = useState(0);

    const { data: itemsData = [], isLoading } = useComplianceItems(pursuitId, {
        refetchInterval: (query: any) => {
            if (query.state.data && (query.state.data as any[]).length > 0) return false;
            // Stop polling after 15 attempts (45 seconds)
            if (pollCount > 15) return false;
            return 3000;
        }
    });

    const items = itemsData as any[];

    useEffect(() => {
        if (items.length === 0 && pollCount <= 15) {
            const timer = setTimeout(() => setPollCount(p => p + 1), 3000);
            return () => clearTimeout(timer);
        }
    }, [items.length, pollCount]);

    const updateItemMutation = useUpdateComplianceItem();
    const deleteItemMutation = useDeleteComplianceItem();
    const createItemsMutation = useCreateComplianceItems();

    // AI Extraction State
    const [isManualExtracting, setIsManualExtracting] = useState(false);
    const showAutoExtracting = items.length === 0 && pollCount <= 15;

    // Local state for AI extraction trigger
    // Actually we should use the API directly or a mutation for extraction if it's an action
    // But useAiCompliance is a query. 
    // Let's use the API method directly in the handler.
    const handleExtractRequirements = async () => {
        setIsManualExtracting(true);
        try {
            // Import api here to avoid hook rules calling
            const { aiApi } = await import('../../services/api');
            const data = await aiApi.getCompliance(importedItemId);

            if (data && data.items) {
                const mappedItems = data.items.map((i: any) => ({
                    requirement: i.requirement,
                    category: i.category,
                    is_pass_fail: i.is_pass_fail,
                    status: 'pending',
                }));

                await createItemsMutation.mutateAsync({
                    pursuitId,
                    items: mappedItems
                });

                toast({ title: 'Requirements Extracted', description: `Added ${mappedItems.length} items to checklist.`, status: 'success' });
            }
        } catch (error) {
            toast({ title: 'Extraction Failed', description: 'Could not extract requirements from AI.', status: 'error' });
        } finally {
            setIsManualExtracting(false);
            setPollCount(16); // force stop auto-polling if manual was triggered
        }
    };

    const handleStatusChange = (id: number, newStatus: string) => {
        updateItemMutation.mutate({ id, data: { status: newStatus } });
    };

    const handleDelete = (id: number) => {
        if (confirm('Are you sure you want to delete this requirement?')) {
            deleteItemMutation.mutate(id);
        }
    };

    // Calculate Progress
    const total = items.length;
    const completed = items.filter((i: any) => i.status === 'pass' || i.status === 'waived').length;
    const progress = total > 0 ? (completed / total) * 100 : 0;

    return (
        <Box display="flex" flexDirection="column" gap={6}>

            {/* Header & Actions */}
            <Flex
                justify="space-between"
                align="center"
                p={6}
                bg={panelBg}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
                shadow="sm"
                wrap="wrap"
                gap={4}
            >
                <Box>
                    <Heading size="md" mb={1}>Compliance Checklist</Heading>
                    <Text fontSize="sm" color="gray.500">Track mandatory requirements to ensure a compliant bid.</Text>
                </Box>
                <HStack>
                    <Button
                        leftIcon={<MdAutoAwesome />}
                        colorScheme="purple"
                        variant="ghost"
                        size="sm"
                        isLoading={isManualExtracting}
                        loadingText="Analyzing..."
                        onClick={handleExtractRequirements}
                        isDisabled={items.length > 0 || showAutoExtracting}
                        title="Manually force AI extraction if automatic extraction failed."
                    >
                        Force Re-Extract
                    </Button>
                </HStack>
            </Flex>

            {/* Progress */}
            {items.length > 0 && (
                <Box px={2}>
                    <HStack justify="space-between" mb={2} fontSize="sm">
                        <Text fontWeight="bold">Readiness Score</Text>
                        <Text>{Math.round(progress)}% Compliant</Text>
                    </HStack>
                    <Progress value={progress} colorScheme={progress === 100 ? 'green' : 'blue'} borderRadius="full" size="sm" />
                </Box>
            )}

            {/* Checklist */}
            <VStack spacing={3} align="stretch">
                {isLoading ? (
                    <Flex justify="center" p={10}><Spinner /></Flex>
                ) : items.length === 0 ? (
                    <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        p={10}
                        bg={panelBg}
                        borderRadius="xl"
                        borderStyle="dashed"
                        borderWidth="2px"
                        borderColor={showAutoExtracting ? "purple.300" : "gray.300"}
                    >
                        {showAutoExtracting ? (
                            <>
                                <Spinner color="purple.500" size="xl" mb={4} thickness="3px" />
                                <Text color="purple.600" fontWeight="bold" fontSize="lg">Auto-Extracting Requirements...</Text>
                                <Text fontSize="sm" color="gray.500" mt={2}>Analyzing the RFP documents using AI. This usually takes 10-30 seconds.</Text>
                            </>
                        ) : (
                            <>
                                <Icon as={MdCheckCircle} boxSize={10} color="gray.300" mb={3} />
                                <Text color="gray.500" fontWeight="medium">No requirements tracked yet.</Text>
                                <Text fontSize="sm" color="gray.400" mb={4}>Auto-extraction may have failed or found no requirements.</Text>
                                <Button
                                    size="sm"
                                    leftIcon={<MdAutoAwesome />}
                                    colorScheme="purple"
                                    variant="outline"
                                    onClick={handleExtractRequirements}
                                    isLoading={isManualExtracting}
                                >
                                    Try Manual Extraction
                                </Button>
                            </>
                        )}
                    </Flex>
                ) : (
                    items.map((item: any) => (
                        <ComplianceItemRow
                            key={item.id}
                            item={item}
                            onStatusChange={handleStatusChange}
                            onDelete={handleDelete}
                        />
                    ))
                )}
            </VStack>
        </Box>
    );
}

// Sub-component for individual row
function ComplianceItemRow({ item, onStatusChange, onDelete }: { item: any; onStatusChange: (id: number, s: string) => void, onDelete: (id: number) => void }) {
    const bg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const [isExpanded, setIsExpanded] = useState(false);
    const updateItemMutation = useUpdateComplianceItem(); // For notes

    const handleNotesSave = (notes: string) => {
        updateItemMutation.mutate({ id: item.id, data: { notes } });
    };

    const statusColors: Record<string, string> = {
        pass: 'green',
        fail: 'red',
        waived: 'gray',
        pending: 'yellow'
    };

    return (
        <Box
            p={4}
            bg={bg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={item.status === 'fail' ? 'red.300' : borderColor}
            borderLeftWidth={item.is_pass_fail ? '4px' : '1px'}
            borderLeftColor={item.is_pass_fail ? 'red.500' : borderColor}
            shadow="sm"
            transition="all 0.2s"
            _hover={{ shadow: 'md' }}
        >
            <Flex gap={4} align="start">
                <Menu>
                    <MenuButton
                        as={IconButton}
                        icon={
                            item.status === 'pass' ? <MdCheckCircle /> :
                                item.status === 'fail' ? <MdError /> :
                                    item.status === 'waived' ? <MdRemoveCircle /> : <Box w={3} h={3} borderRadius="full" bg="yellow.400" />
                        }
                        variant="ghost"
                        colorScheme={statusColors[item.status]}
                        size="sm"
                        mt={1}
                    />
                    <MenuList>
                        <MenuItem icon={<MdCheckCircle />} onClick={() => onStatusChange(item.id, 'pass')}>Pass / Met</MenuItem>
                        <MenuItem icon={<MdError />} onClick={() => onStatusChange(item.id, 'fail')}>Fail / Missing</MenuItem>
                        <MenuItem icon={<MdRemoveCircle />} onClick={() => onStatusChange(item.id, 'waived')}>Waived / N/A</MenuItem>
                        <MenuItem onClick={() => onStatusChange(item.id, 'pending')}>Reset to Pending</MenuItem>
                    </MenuList>
                </Menu>

                <Box flex={1}>
                    <HStack mb={1}>
                        <Badge fontSize="xx-small" colorScheme={item.is_pass_fail ? 'red' : 'gray'}>
                            {item.is_pass_fail ? 'MANDATORY' : item.category || 'GENERAL'}
                        </Badge>
                        {item.status !== 'pending' && <Badge colorScheme={statusColors[item.status]} fontSize="xx-small">{item.status.toUpperCase()}</Badge>}
                    </HStack>
                    <Text fontWeight="medium" fontSize="sm" noOfLines={isExpanded ? undefined : 2}>
                        {item.requirement}
                    </Text>
                </Box>

                <HStack>
                    <IconButton
                        aria-label="Notes"
                        icon={<MdEdit />}
                        size="xs"
                        variant="ghost"
                        onClick={() => setIsExpanded(!isExpanded)}
                        isActive={isExpanded || !!item.notes}
                    />
                    <IconButton
                        aria-label="Delete"
                        icon={<MdDelete />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => onDelete(item.id)}
                    />
                </HStack>
            </Flex>

            <Collapse in={isExpanded} animateOpacity>
                <Box mt={3} pt={3} borderTopWidth="1px" borderColor={borderColor}>
                    <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.500">Strategy / Notes</Text>
                    <Textarea
                        placeholder="How will we meet this requirement?"
                        size="sm"
                        rows={3}
                        defaultValue={item.notes}
                        onBlur={(e) => handleNotesSave(e.target.value)}
                    />
                </Box>
            </Collapse>
        </Box>
    );
}
